import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryService } from '../../services/inventoryService';
import { profileService } from '../../services/profileService';
import { greenBeanService } from '../../services/greenBeanService';
import type { InventoryItem, InventoryLot, InventorySample } from '../../types/inventory';
import type { GreenBeanProfile } from '../../types/greenBean';
import { useLanguage } from '../../features/language/useLanguage';

export type InventoryItemDetailsTab = 'lots' | 'sample' | 'ingredients';

interface QCModalState {
  isOpen: boolean;
  type: 'bean' | 'cupping';
  entityId: string;
  entityType: 'LotType' | 'SampleType' | 'ItemType';
  lotId?: string;
  readOnly?: boolean;
}

interface RoastModalContext {
  referenceId: string;
  label: string;
  readOnly?: boolean;
}

export const useInventoryItemDetailsState = (item: InventoryItem) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<InventoryItemDetailsTab>(
    item.itemType === 'finished_good' ? 'ingredients' : 'lots'
  );
  const [lots, setLots] = useState<InventoryLot[]>([]);
  const [samples, setSamples] = useState<InventorySample[]>([]);
  const [ingredients, setIngredients] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isCreateSampleOpen, setIsCreateSampleOpen] = useState(false);
  const [qcModal, setQcModal] = useState<QCModalState>({
    isOpen: false,
    type: 'bean',
    entityId: '',
    entityType: 'LotType',
  });
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedLotId, setSelectedLotId] = useState<string | undefined>(undefined);
  const [selectedLotNumber, setSelectedLotNumber] = useState<string | undefined>(undefined);
  const [roastModalContext, setRoastModalContext] = useState<RoastModalContext | null>(null);

  const { data: cuppingProfilesData, refetch: refetchCupping } = useQuery({
    queryKey: ['cupping-profiles'],
    queryFn: profileService.getAllCuppingProfiles,
  });

  const { data: greenBeanProfilesData, refetch: refetchGreenBeans } = useQuery<GreenBeanProfile[]>({
    queryKey: ['green-beans'],
    queryFn: greenBeanService.getAll,
  });

  const cuppingProfiles = useMemo(() => cuppingProfilesData || [], [cuppingProfilesData]);
  const greenBeanProfiles = useMemo(() => greenBeanProfilesData || [], [greenBeanProfilesData]);

  useEffect(() => {
    if (item.itemType === 'finished_good' && activeTab !== 'ingredients') {
      setActiveTab('ingredients');
    } else if (item.itemType !== 'finished_good' && activeTab === 'ingredients') {
      setActiveTab('lots');
    }
  }, [item.itemType, activeTab]);

  const fetchData = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setError('');
      try {
        const lotsPromise = inventoryService.getLots(item.id).catch(err => {
          console.warn('Failed to fetch lots:', err);
          return [];
        });
        const samplesPromise = inventoryService.getSamples(item.id).catch(err => {
          console.warn('Failed to fetch samples:', err);
          return [];
        });
        const itemPromise = inventoryService.getById(item.id).catch(err => {
          console.warn('Failed to fetch item details:', err);
          return null;
        });

        const [lotsData, samplesData, itemData] = await Promise.all([lotsPromise, samplesPromise, itemPromise]);

        setLots(lotsData);
        setSamples(samplesData);
        if (itemData && itemData.ingredients) {
          setIngredients(itemData.ingredients);
        }
      } catch (e) {
        console.error('Unexpected error in fetchData', e);
        setError(t('errors.common.loadDataFailed'));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [item.id, t]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData, item.id, item.stock, item.status]);

  const showSamplesTab = true;

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    const time = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Jakarta',
    }).format(date);
    const day = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      timeZone: 'Asia/Jakarta',
    }).format(date);
    return `${time}, ${day}`;
  };

  const getGreenBeanQC = (entityId: string, entityType: 'LotType' | 'SampleType') => {
    const profile =
      entityType === 'LotType'
        ? greenBeanProfiles.find(profileItem => profileItem.lot_id === entityId)
        : greenBeanProfiles.find(profileItem => profileItem.sample_id === entityId);
    return profile?.sample_physical_check;
  };


  const handleQCClick = (
    type: 'bean' | 'cupping',
    entityId: string,
    entityType: 'LotType' | 'SampleType' | 'ItemType',
    lotId?: string,
    readOnly?: boolean
  ) => {
    setQcModal({
      isOpen: true,
      type,
      entityId,
      entityType,
      lotId,
      readOnly,
    });
  };

  const getInitialQCData = () => {
    const { type, entityId, entityType } = qcModal;
    if (type === 'bean') {
      if (entityType === 'ItemType') {
        const ingredient = ingredients.find(ingredientItem => ingredientItem.id === entityId);
        return ingredient?.greenBeanProfiles?.find(profile => profile.type === 'ItemType');
      }
      return entityType === 'LotType'
        ? greenBeanProfiles.find(profile => profile.lot_id === entityId)
        : greenBeanProfiles.find(profile => profile.sample_id === entityId);
    }
    if (entityType === 'ItemType') {
      const ingredient = ingredients.find(ingredientItem => ingredientItem.id === entityId);
      return ingredient?.cuppingProfiles?.find(profile => profile.type === 'ItemType');
    }
    return entityType === 'LotType'
      ? cuppingProfiles.find(profile => profile.lot_id === entityId)
      : cuppingProfiles.find(profile => profile.sample_id === entityId);
  };

  const handleCreateSample = (newSample: InventorySample) => {
    setSamples(prev => [newSample, ...prev]);
    setActiveTab('sample');
  };

  const handleHistoryClick = (lotId: string, lotNumber: string) => {
    setSelectedLotId(lotId);
    setSelectedLotNumber(lotNumber);
    setIsHistoryOpen(true);
  };

  const handleHistoryUpdate = async () => {
    await fetchData();
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
  };

  return {
    activeTab,
    setActiveTab,
    lots,
    samples,
    ingredients,
    loading,
    error,
    isCreateSampleOpen,
    setIsCreateSampleOpen,
    qcModal,
    setQcModal,
    isHistoryOpen,
    setIsHistoryOpen,
    selectedLotId,
    selectedLotNumber,
    roastModalContext,
    setRoastModalContext,
    cuppingProfiles,
    greenBeanProfiles,
    refetchCupping,
    refetchGreenBeans,
    fetchData,
    showSamplesTab,
    formatDate,
    getGreenBeanQC,
    handleQCClick,
    getInitialQCData,
    handleCreateSample,
    handleHistoryClick,
    handleHistoryUpdate,
  };
};
